/**
 * FaithWorksTVAdmin — iter-426 foundation.
 * Compact admin portal at /admin/fwtv covering Content (CMS), Programmes,
 * Speakers, Sermons. Mirrors the pattern from BookshopAdmin.jsx.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Edit2, Trash2, Radio, Tv, Mic, Users, FileText, Wifi, Play, Square, Send, Layers, ListMusic, ArrowUp, ArrowDown, X, Brain, Heart, BarChart3 } from 'lucide-react';
import api from '@/lib/api';
import { Intelligence } from './fwtv/Intelligence';
import { PrayerInbox } from './fwtv/PrayerInbox';
import { Analytics as AnalyticsTab } from './fwtv/Analytics';
import { MediaUpload } from '@/components/MediaUpload';

const getErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) {
    // Pydantic 422 detail: [{type, loc, msg, input, url}, ...]
    return d.map((x) => {
      if (x && typeof x === 'object') {
        const loc = Array.isArray(x.loc) ? x.loc.filter((p) => p !== 'body').join('.') : '';
        return loc ? `${loc}: ${x.msg}` : (x.msg || 'Invalid input');
      }
      return String(x);
    }).join(' · ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return d || e?.message || 'Error';
};

export default function FaithWorksTVAdmin() {
  const { data: dash } = useQuery({
    queryKey: ['fwtv-dashboard'],
    queryFn: async () => (await api.get('/fwtv/admin/dashboard')).data,
  });
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Tv className="h-6 w-6" /> Faith Works TV</h1>
          <p className="text-sm text-slate-500">Backend for <code>faithworldministries.com/faith-works-tv/</code></p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <a href="/admin/fwtv/studio" data-testid="fwtv-studio-admin-link"
             className="rounded-full border border-slate-300 bg-slate-50 px-3 py-1 font-medium text-slate-800 hover:bg-slate-100">
            Studio: pipeline, schedule &amp; team →
          </a>
          <a href="/admin/fwtv/premium" data-testid="fwtv-premium-admin-link"
             className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 font-medium text-amber-800 hover:bg-amber-100">
            Premium, YouTube &amp; subscribers →
          </a>
          <a href="/tv" target="_blank" rel="noopener noreferrer" data-testid="fwtv-public-link"
             className="rounded-full border px-3 py-1 hover:bg-slate-50">View public TV</a>
          <Badge variant="outline">{dash?.programmes_published || 0} programmes</Badge>
          <Badge variant="outline">{dash?.sermons_published || 0} sermons</Badge>
          <Badge variant="outline">{dash?.speakers || 0} speakers</Badge>
          {dash?.live_now && <Badge className="bg-red-500 text-white animate-pulse">LIVE NOW</Badge>}
        </div>
      </div>
      <Tabs defaultValue="content">
        <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
          <TabsTrigger value="content" data-testid="fwtv-tab-content"><FileText className="h-4 w-4 mr-1" /> Content</TabsTrigger>
          <TabsTrigger value="programmes" data-testid="fwtv-tab-programmes"><Radio className="h-4 w-4 mr-1" /> Programmes</TabsTrigger>
          <TabsTrigger value="sermons" data-testid="fwtv-tab-sermons"><Mic className="h-4 w-4 mr-1" /> Sermons</TabsTrigger>
          <TabsTrigger value="speakers" data-testid="fwtv-tab-speakers"><Users className="h-4 w-4 mr-1" /> Speakers</TabsTrigger>
          <TabsTrigger value="categories" data-testid="fwtv-tab-categories"><Layers className="h-4 w-4 mr-1" /> Categories</TabsTrigger>
          <TabsTrigger value="playlists" data-testid="fwtv-tab-playlists"><ListMusic className="h-4 w-4 mr-1" /> Playlists</TabsTrigger>
          <TabsTrigger value="live" data-testid="fwtv-tab-live"><Wifi className="h-4 w-4 mr-1" /> Live</TabsTrigger>
          <TabsTrigger value="intelligence" data-testid="fwtv-tab-intelligence"><Brain className="h-4 w-4 mr-1" /> Intelligence</TabsTrigger>
          <TabsTrigger value="prayer" data-testid="fwtv-tab-prayer"><Heart className="h-4 w-4 mr-1" /> Prayer</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="fwtv-tab-analytics"><BarChart3 className="h-4 w-4 mr-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="embed" data-testid="fwtv-tab-embed">Embed</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-4"><ContentBlocks /></TabsContent>
        <TabsContent value="programmes" className="mt-4"><Programmes /></TabsContent>
        <TabsContent value="sermons" className="mt-4"><Sermons /></TabsContent>
        <TabsContent value="speakers" className="mt-4"><Speakers /></TabsContent>
        <TabsContent value="categories" className="mt-4"><Categories /></TabsContent>
        <TabsContent value="playlists" className="mt-4"><Playlists /></TabsContent>
        <TabsContent value="live" className="mt-4"><LiveBroadcast /></TabsContent>
        <TabsContent value="intelligence" className="mt-4"><Intelligence /></TabsContent>
        <TabsContent value="prayer" className="mt-4"><PrayerInbox /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
        <TabsContent value="embed" className="mt-4"><EmbedInstructions /></TabsContent>
      </Tabs>
    </div>
  );
}

const SLOT_LABELS = {
  hero: 'Hero', live_now: 'Live Now', upcoming_intro: 'Upcoming intro',
  latest_messages_intro: 'Latest messages intro',
  featured_sermons_intro: 'Featured sermons intro',
  trending_intro: 'Trending intro',
  conference_highlights_intro: 'Conference highlights intro',
  bible_college_intro: 'Bible College intro',
  testimonies_intro: 'Testimonies intro',
  podcasts_intro: 'Podcasts intro',
  schedule_intro: 'Schedule intro',
  speakers_intro: 'Speakers intro',
  prayer_cta: 'Prayer CTA',
  giving_cta: 'Giving CTA',
  newsletter: 'Newsletter CTA',
  footer_note: 'Footer note',
  partner_hero: 'Partner page hero',
};

const ContentBlocks = () => {
  const qc = useQueryClient();
  const [dirty, setDirty] = useState({});
  const [savingSlot, setSavingSlot] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-content'],
    queryFn: async () => (await api.get('/fwtv/admin/content')).data,
  });
  const blocks = (data?.blocks || []).map((b) => ({ ...b, ...(dirty[b.slot] || {}) }));
  const setF = (slot, k, v) => setDirty((p) => ({ ...p, [slot]: { ...(p[slot] || {}), [k]: v } }));
  const save = async (slot) => {
    const patch = dirty[slot];
    if (!patch) return toast.info('Nothing to save');
    setSavingSlot(slot);
    try {
      await api.put(`/fwtv/admin/content/${slot}`, patch);
      toast.success(`${SLOT_LABELS[slot] || slot} saved`);
      setDirty((p) => { const c = { ...p }; delete c[slot]; return c; });
      qc.invalidateQueries({ queryKey: ['fwtv-content'] });
    } catch (e) { toast.error(getErr(e)); } finally { setSavingSlot(''); }
  };
  if (isLoading) return <Loader2 className="animate-spin mx-auto h-6 w-6" />;
  return (
    <div className="space-y-2">
      {blocks.map((b) => (
        <Card key={b.slot} data-testid={`fwtv-block-${b.slot}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-sm">{SLOT_LABELS[b.slot] || b.slot} {!b.is_active && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">Hidden</Badge>}{dirty[b.slot] && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px]">Unsaved</Badge>}</CardTitle>
                <CardDescription className="text-xs">slot: <code>{b.slot}</code></CardDescription>
              </div>
              <div className="flex gap-2">
                <label className="flex items-center gap-1 text-xs"><Switch checked={!!b.is_active} onCheckedChange={(v) => setF(b.slot, 'is_active', v)} data-testid={`fwtv-active-${b.slot}`} /> Active</label>
                <Button size="sm" onClick={() => save(b.slot)} disabled={!dirty[b.slot] || savingSlot === b.slot} data-testid={`fwtv-save-${b.slot}`}>{savingSlot === b.slot ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input placeholder="Title" value={b.title || ''} onChange={(e) => setF(b.slot, 'title', e.target.value)} data-testid={`fwtv-title-${b.slot}`} />
            <Input placeholder="Subtitle" value={b.subtitle || ''} onChange={(e) => setF(b.slot, 'subtitle', e.target.value)} data-testid={`fwtv-subtitle-${b.slot}`} />
            <Textarea rows={2} placeholder="Body" value={b.body || ''} onChange={(e) => setF(b.slot, 'body', e.target.value)} className="md:col-span-2" data-testid={`fwtv-body-${b.slot}`} />
            <MediaUpload value={b.image_url || ''} onChange={(url) => setF(b.slot, 'image_url', url)} purpose="cover" label="Image" testId={`fwtv-image-${b.slot}`} previewClassName="h-20" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="CTA label" value={b.cta_label || ''} onChange={(e) => setF(b.slot, 'cta_label', e.target.value)} data-testid={`fwtv-cta-label-${b.slot}`} />
              <Input placeholder="CTA URL" value={b.cta_url || ''} onChange={(e) => setF(b.slot, 'cta_url', e.target.value)} data-testid={`fwtv-cta-url-${b.slot}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ---- Generic simple CRUD used by Programmes/Sermons/Speakers ----
const SimpleCrud = ({ resource, testPrefix, columns, fields, listKey }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-' + resource],
    queryFn: async () => (await api.get(`/fwtv/admin/${resource}`)).data,
  });
  const rows = data?.[listKey] || [];
  const del = useMutation({
    mutationFn: (id) => api.delete(`/fwtv/admin/${resource}/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['fwtv-' + resource] }); },
    onError: (e) => toast.error(getErr(e)),
  });
  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <h2 className="text-lg font-semibold capitalize">{resource}</h2>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid={`${testPrefix}-new`}><Plus className="h-4 w-4 mr-1" /> New</Button>
      </div>
      {isLoading ? <Loader2 className="animate-spin mx-auto" /> : (
        rows.length === 0 ? <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">None yet.</CardContent></Card>
        : (
          <div className="border rounded overflow-x-auto"><table className="w-full text-xs">
            <thead className="bg-slate-50"><tr>{columns.map((c) => <th key={c.k} className="text-left p-2">{c.label}</th>)}<th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`${testPrefix}-row-${r.id}`}>
                {columns.map((c) => <td key={c.k} className="p-2">{c.render ? c.render(r) : r[c.k]}</td>)}
                <td className="p-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }} data-testid={`${testPrefix}-edit-${r.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm('Delete?')) del.mutate(r.id); }} data-testid={`${testPrefix}-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )
      )}
      <EditorDialog open={open} onClose={() => setOpen(false)} initial={editing} fields={fields}
        onSave={async (payload) => {
          try {
            if (editing?.id) await api.put(`/fwtv/admin/${resource}/${editing.id}`, payload);
            else await api.post(`/fwtv/admin/${resource}`, payload);
            toast.success(editing ? 'Updated' : 'Created');
            setOpen(false);
            qc.invalidateQueries({ queryKey: ['fwtv-' + resource] });
          } catch (e) { toast.error(getErr(e)); }
        }} testPrefix={testPrefix} />
    </div>
  );
};

const EditorDialog = ({ open, onClose, initial, fields, onSave, testPrefix }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    const base = {};
    fields.forEach((f) => {
      // Typed defaults so untouched fields don't send '' → Pydantic 422.
      let d = f.default;
      if (d == null) {
        if (f.type === 'switch') d = false;
        else if (f.type === 'number') d = '';
        else d = '';
      }
      base[f.k] = d;
    });
    if (initial) fields.forEach((f) => { if (initial[f.k] != null) base[f.k] = initial[f.k]; });
    setForm(base);
  }, [initial, open, fields]);
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        const v = form[f.k];
        if (f.type === 'switch') {
          payload[f.k] = !!v;
        } else if (f.type === 'number') {
          if (v === '' || v == null) return; // omit — backend treats as null
          const n = Number(v);
          if (!Number.isNaN(n)) payload[f.k] = n;
        } else if (f.type === 'select') {
          if (v === '' || v == null) return;
          payload[f.k] = v;
        } else if (f.type === 'select-async') {
          if (v === '' || v == null) return;
          payload[f.k] = v;
        } else if (f.type === 'multi-select-async') {
          payload[f.k] = Array.isArray(v) ? v : [];
        } else {
          if (v === '' || v == null) return;
          payload[f.k] = v;
        }
      });
      await onSave(payload);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testPrefix}-editor`}>
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'New'}</DialogTitle><DialogDescription>All fields optional unless marked *</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {fields.map((f) => (
            <div key={f.k}>
              <Label className="text-xs">{f.label}{f.required && ' *'}</Label>
              {f.type === 'textarea' ? (
                <Textarea rows={3} value={form[f.k] || ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`${testPrefix}-field-${f.k}`} />
              ) : f.type === 'select' ? (
                <select value={form[f.k] || ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="w-full border rounded h-9 px-2 text-sm" data-testid={`${testPrefix}-field-${f.k}`}>
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'select-async' ? (
                <AsyncSelect endpoint={f.endpoint} listKey={f.listKey}
                                 valueKey={f.valueKey || 'id'} labelKey={f.labelKey || 'name'}
                                 secondaryKey={f.secondaryKey}
                                 value={form[f.k] || ''} placeholder={f.placeholder}
                                 onChange={(v) => setForm({ ...form, [f.k]: v })}
                                 testId={`${testPrefix}-field-${f.k}`} />
              ) : f.type === 'multi-select-async' ? (
                <MultiAsyncSelect endpoint={f.endpoint} listKey={f.listKey}
                                      valueKey={f.valueKey || 'id'} labelKey={f.labelKey || 'name'}
                                      secondaryKey={f.secondaryKey}
                                      value={form[f.k] || []} placeholder={f.placeholder}
                                      onChange={(v) => setForm({ ...form, [f.k]: v })}
                                      testId={`${testPrefix}-field-${f.k}`} />
              ) : f.type === 'media' ? (
                <MediaUpload value={form[f.k] ?? ''} onChange={(url) => setForm({ ...form, [f.k]: url })}
                             purpose={f.purpose || 'photo'} testId={`${testPrefix}-field-${f.k}`}
                             previewClassName="h-20" />
              ) : f.type === 'switch' ? (
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!form[f.k]} onCheckedChange={(v) => setForm({ ...form, [f.k]: v })} data-testid={`${testPrefix}-field-${f.k}`} /> {f.label}</label>
              ) : (
                <Input type={f.type || 'text'} value={form[f.k] ?? ''} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.placeholder} data-testid={`${testPrefix}-field-${f.k}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={`${testPrefix}-save`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_OPTS = [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }, { value: 'archived', label: 'Archived' }];


// ---- iter-435h bridge helpers ----
function useAsyncOptions(endpoint, listKey) {
  return useQuery({
    queryKey: ['async-opts', endpoint, listKey],
    queryFn: async () => {
      const r = await api.get(endpoint);
      return listKey ? (r.data?.[listKey] || []) : (Array.isArray(r.data) ? r.data : []);
    },
    staleTime: 60_000,
  });
}


function AsyncSelect({ endpoint, listKey, valueKey, labelKey, secondaryKey,
                          value, placeholder, onChange, testId }) {
  const { data: options = [], isLoading } = useAsyncOptions(endpoint, listKey);
  return (
    <select value={value || ''}
              onChange={(e) => onChange(e.target.value || null)}
              className="w-full border rounded h-9 px-2 text-sm bg-white"
              data-testid={testId} disabled={isLoading}>
      <option value="">{isLoading ? 'Loading…' : (placeholder || '— none —')}</option>
      {options.map((o) => (
        <option key={o[valueKey]} value={o[valueKey]}>
          {o[labelKey] || o[valueKey]}
          {secondaryKey && o[secondaryKey] ? ` · ${o[secondaryKey]}` : ''}
        </option>
      ))}
    </select>
  );
}


function MultiAsyncSelect({ endpoint, listKey, valueKey, labelKey, secondaryKey,
                              value, placeholder, onChange, testId }) {
  const { data: options = [], isLoading } = useAsyncOptions(endpoint, listKey);
  const selected = Array.isArray(value) ? value : [];
  const byId = React.useMemo(() =>
    Object.fromEntries(options.map((o) => [o[valueKey], o])), [options, valueKey]);
  const [pick, setPick] = useState('');
  const add = (id) => { if (id && !selected.includes(id)) onChange([...selected, id]); };
  const remove = (id) => onChange(selected.filter((x) => x !== id));
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex gap-1 flex-wrap min-h-[28px] p-1 border rounded bg-white">
        {selected.length === 0 && (
          <span className="text-xs text-slate-400 px-1">{placeholder || 'None linked yet'}</span>
        )}
        {selected.map((id) => {
          const o = byId[id];
          return (
            <Badge key={id} variant="outline"
                     className="text-[10px] flex items-center gap-1"
                     data-testid={`${testId}-chip-${id}`}>
              {(o?.[labelKey]) || id.slice(0, 8) + '…'}
              <button type="button" onClick={() => remove(id)}
                        className="text-slate-400 hover:text-rose-500 ml-1"
                        data-testid={`${testId}-remove-${id}`}>×</button>
            </Badge>
          );
        })}
      </div>
      <select value={pick}
                onChange={(e) => { add(e.target.value); setPick(''); }}
                className="w-full border rounded h-8 px-2 text-xs bg-white"
                data-testid={`${testId}-picker`} disabled={isLoading}>
        <option value="">{isLoading ? 'Loading…' : '+ Add link…'}</option>
        {options.filter((o) => !selected.includes(o[valueKey])).map((o) => (
          <option key={o[valueKey]} value={o[valueKey]}>
            {o[labelKey] || o[valueKey]}
            {secondaryKey && o[secondaryKey] ? ` · ${o[secondaryKey]}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

const Programmes = () => <SimpleCrud resource="programmes" testPrefix="fwtv-prog" listKey="programmes"
  columns={[
    { k: 'title', label: 'Title' },
    { k: 'presenter_name', label: 'Presenter' },
    { k: 'broadcast_time', label: 'Broadcast', render: (r) => r.broadcast_time ? new Date(r.broadcast_time).toLocaleString() : '' },
    { k: 'status', label: 'Status', render: (r) => <Badge variant="outline" className="text-[10px]">{r.status}</Badge> },
    { k: 'is_live', label: 'Live?', render: (r) => r.is_live ? <Badge className="bg-red-500 text-white text-[10px]">LIVE</Badge> : '' },
  ]}
  fields={[
    { k: 'title', label: 'Title', required: true },
    { k: 'description', label: 'Description', type: 'textarea' },
    { k: 'presenter_name', label: 'Presenter name' },
    { k: 'category', label: 'Category' },
    { k: 'image_url', label: 'Image', type: 'media', purpose: 'cover' },
    { k: 'video_url', label: 'Video link (YouTube/Vimeo/Cloudflare Stream)' },
    { k: 'trailer_url', label: 'Trailer', type: 'media', purpose: 'video' },
    { k: 'broadcast_time', label: 'Broadcast time (ISO)', placeholder: '2026-04-01T18:00:00Z' },
    { k: 'duration_minutes', label: 'Duration (min)', type: 'number' },
    { k: 'timezone_name', label: 'Timezone', default: 'UTC' },
    { k: 'repeats', label: 'Repeats', type: 'select', options: [{value:'',label:'None'},{value:'daily',label:'Daily'},{value:'weekly',label:'Weekly'},{value:'monthly',label:'Monthly'}] },
    { k: 'is_live', label: 'Live now', type: 'switch' },
    { k: 'is_featured', label: 'Featured', type: 'switch' },
    { k: 'status', label: 'Status', type: 'select', options: STATUS_OPTS, default: 'draft' },
    // iter-435h bridge — link to Events (multi-select)
    { k: 'event_ids', label: 'Linked events', type: 'multi-select-async',
      endpoint: '/admin/events', valueKey: 'id', labelKey: 'name',
      secondaryKey: 'slug',
      placeholder: 'Pick events this programme is broadcast at' },
  ]} />;

const Sermons = () => <SimpleCrud resource="sermons" testPrefix="fwtv-serm" listKey="sermons"
  columns={[
    { k: 'title', label: 'Title' },
    { k: 'publication_date', label: 'Published', render: (r) => r.publication_date ? new Date(r.publication_date).toLocaleDateString() : '' },
    { k: 'duration_seconds', label: 'Duration', render: (r) => r.duration_seconds ? Math.round(r.duration_seconds/60) + ' min' : '' },
    { k: 'status', label: 'Status', render: (r) => <Badge variant="outline" className="text-[10px]">{r.status}</Badge> },
  ]}
  fields={[
    { k: 'title', label: 'Title', required: true },
    { k: 'description', label: 'Description', type: 'textarea' },
    { k: 'speaker_id', label: 'Speaker ID (from Speakers tab)' },
    { k: 'series_id', label: 'Series ID (optional)' },
    { k: 'category', label: 'Category' },
    { k: 'thumbnail_url', label: 'Thumbnail', type: 'media', purpose: 'cover' },
    { k: 'video_url', label: 'Video', type: 'media', purpose: 'video' },
    { k: 'audio_url', label: 'Audio', type: 'media', purpose: 'audio' },
    { k: 'duration_seconds', label: 'Duration (seconds)', type: 'number' },
    { k: 'publication_date', label: 'Publication date (ISO)' },
    { k: 'language', label: 'Language', default: 'en' },
    { k: 'is_featured', label: 'Featured', type: 'switch' },
    { k: 'is_podcast', label: 'Include in podcast feed', type: 'switch', default: true },
    { k: 'status', label: 'Status', type: 'select', options: STATUS_OPTS, default: 'draft' },
    // iter-435h bridge — attach a donation campaign for the "Give now" CTA
    { k: 'donation_campaign_id', label: 'Donation campaign (Give-now CTA)',
      type: 'select-async', endpoint: '/campaigns',
      valueKey: 'id', labelKey: 'name', secondaryKey: 'slug',
      placeholder: '— none —' },
  ]} />;

const Speakers = () => <SimpleCrud resource="speakers" testPrefix="fwtv-spk" listKey="speakers"
  columns={[
    { k: 'name', label: 'Name' },
    { k: 'role', label: 'Role' },
    { k: 'is_featured', label: 'Featured', render: (r) => r.is_featured ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">Featured</Badge> : '' },
  ]}
  fields={[
    { k: 'name', label: 'Name', required: true },
    { k: 'role', label: 'Role / title' },
    { k: 'ministry', label: 'Ministry' },
    { k: 'photo_url', label: 'Photo', type: 'media', purpose: 'avatar' },
    { k: 'bio', label: 'Bio', type: 'textarea' },
    { k: 'website_url', label: 'Website URL' },
    { k: 'is_featured', label: 'Featured', type: 'switch' },
    // iter-435h bridge — churches this speaker serves
    { k: 'church_ids', label: 'Linked churches', type: 'multi-select-async',
      endpoint: '/admin/churches', valueKey: 'id', labelKey: 'name',
      secondaryKey: 'city',
      placeholder: 'Pick churches this speaker serves' },
  ]} />;

const Categories = () => <SimpleCrud resource="categories" testPrefix="fwtv-cat" listKey="categories"
  columns={[
    { k: 'label', label: 'Label' },
    { k: 'slug', label: 'Slug' },
    { k: 'sort_order', label: 'Order' },
    { k: 'is_active', label: 'Active', render: (r) => r.is_active !== false ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">active</Badge> : <Badge variant="outline" className="text-[10px]">hidden</Badge> },
  ]}
  fields={[
    { k: 'label', label: 'Label', required: true },
    { k: 'slug', label: 'Slug (optional — auto from label)', placeholder: 'sunday-service' },
    { k: 'description', label: 'Description', type: 'textarea' },
    { k: 'image_url', label: 'Image', type: 'media', purpose: 'cover' },
    { k: 'sort_order', label: 'Sort order', type: 'number', default: 0 },
    { k: 'is_active', label: 'Active', type: 'switch', default: true },
  ]} />;

// ============== PLAYLISTS (Phase 3) ==============
// Custom UI (not SimpleCrud) because playlists need an ordered-sermon
// picker with reordering + drag-out.
const Playlists = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-playlists'],
    queryFn: async () => (await api.get('/fwtv/admin/playlists')).data,
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/fwtv/admin/playlists/${id}`)).data,
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['fwtv-playlists'] }); },
    onError: (e) => toast.error(getErr(e)),
  });

  const rows = data?.playlists || [];
  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <h2 className="text-lg font-semibold">Playlists</h2>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="fwtv-pl-new"><Plus className="h-4 w-4 mr-1" /> New</Button>
      </div>
      {isLoading ? <Loader2 className="animate-spin mx-auto" /> : rows.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No playlists yet.</CardContent></Card>
      ) : (
        <div className="border rounded overflow-x-auto"><table className="w-full text-xs">
          <thead className="bg-slate-50"><tr><th className="text-left p-2">Title</th><th className="text-left p-2">Slug</th><th className="text-left p-2">Episodes</th><th className="text-left p-2">Status</th><th /></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`fwtv-pl-row-${r.id}`}>
              <td className="p-2">{r.title}{r.is_featured && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">Featured</Badge>}</td>
              <td className="p-2 font-mono">{r.slug}</td>
              <td className="p-2">{(r.sermon_ids || []).length}</td>
              <td className="p-2"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></td>
              <td className="p-2 text-right space-x-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }} data-testid={`fwtv-pl-edit-${r.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm('Delete?')) del.mutate(r.id); }} data-testid={`fwtv-pl-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <PlaylistEditor open={open} onClose={() => setOpen(false)} initial={editing}
        onSaved={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['fwtv-playlists'] }); }} />
    </div>
  );
};

const PlaylistEditor = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = useState({ title: '', description: '', cover_image: '', status: 'draft', is_featured: false, sermon_ids: [] });
  const [saving, setSaving] = useState(false);
  const { data: sermonsData } = useQuery({
    queryKey: ['fwtv-sermons'],
    queryFn: async () => (await api.get('/fwtv/admin/sermons')).data,
  });
  const sermons = sermonsData?.sermons || [];
  const sermonById = React.useMemo(() => Object.fromEntries(sermons.map((s) => [s.id, s])), [sermons]);

  React.useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || '', description: initial.description || '',
        cover_image: initial.cover_image || '', status: initial.status || 'draft',
        is_featured: !!initial.is_featured, sermon_ids: initial.sermon_ids || [],
      });
    } else {
      setForm({ title: '', description: '', cover_image: '', status: 'draft', is_featured: false, sermon_ids: [] });
    }
  }, [initial, open]);

  const move = (idx, dir) => {
    const next = [...form.sermon_ids];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setForm({ ...form, sermon_ids: next });
  };
  const remove = (idx) => setForm({ ...form, sermon_ids: form.sermon_ids.filter((_, i) => i !== idx) });
  const add = (id) => { if (!form.sermon_ids.includes(id)) setForm({ ...form, sermon_ids: [...form.sermon_ids, id] }); };

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, cover_image: form.cover_image || null,
        status: form.status, is_featured: !!form.is_featured, sermon_ids: form.sermon_ids,
      };
      if (initial?.id) await api.put(`/fwtv/admin/playlists/${initial.id}`, payload);
      else await api.post('/fwtv/admin/playlists', payload);
      toast.success(initial ? 'Playlist updated' : 'Playlist created');
      onSaved();
    } catch (e) { toast.error(getErr(e)); }
    finally { setSaving(false); }
  };

  const available = sermons.filter((s) => !form.sermon_ids.includes(s.id));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fwtv-pl-editor">
        <DialogHeader><DialogTitle>{initial ? 'Edit playlist' : 'New playlist'}</DialogTitle><DialogDescription>Order matters — this is the order the public site and the podcast RSS will use.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="fwtv-pl-field-title" /></div>
          <div><MediaUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} purpose="cover" label="Cover image" testId="fwtv-pl-field-cover" previewClassName="h-20" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fwtv-pl-field-desc" /></div>
          <div>
            <Label className="text-xs">Status</Label>
            <select className="w-full border rounded h-9 px-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="fwtv-pl-field-status">
              {STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm mt-6"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="fwtv-pl-field-featured" /> Featured</label>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Sermons in this playlist ({form.sermon_ids.length})</Label>
          {form.sermon_ids.length === 0 ? (
            <p className="text-xs text-slate-400 py-3">No sermons yet — pick from the list below.</p>
          ) : (
            <ol className="border rounded divide-y" data-testid="fwtv-pl-sermon-list">
              {form.sermon_ids.map((id, idx) => {
                const s = sermonById[id];
                return (
                  <li key={id} className="flex items-center gap-2 p-2 text-sm" data-testid={`fwtv-pl-item-${id}`}>
                    <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                    <span className="flex-1">{s ? s.title : <em className="text-slate-400">missing / unpublished ({id})</em>}</span>
                    <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`fwtv-pl-up-${id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === form.sermon_ids.length - 1} data-testid={`fwtv-pl-down-${id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(idx)} data-testid={`fwtv-pl-remove-${id}`}><X className="h-3.5 w-3.5" /></Button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="mt-3">
          <Label className="text-xs">Add sermons</Label>
          {available.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No more sermons available.</p>
          ) : (
            <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1" data-testid="fwtv-pl-available-list">
              {available.slice(0, 50).map((s) => (
                <div key={s.id} className="flex items-center justify-between text-xs">
                  <span>{s.title} <Badge variant="outline" className="text-[10px] ml-1">{s.status}</Badge></span>
                  <Button size="sm" variant="outline" onClick={() => add(s.id)} data-testid={`fwtv-pl-add-${s.id}`}><Plus className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="fwtv-pl-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save playlist'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// ============== LIVE BROADCAST (Phase 2) ==============
const LiveBroadcast = () => {
  const qc = useQueryClient();
  const [connId, setConnId] = useState('');
  const [progId, setProgId] = useState('');
  const [streamUrl, setStreamUrl] = useState('');
  const [scene, setScene] = useState('');
  const [startStream, setStartStream] = useState(false);
  const [ltSource, setLtSource] = useState('LowerThird');
  const [ltText, setLtText] = useState('');
  const [obsForm, setObsForm] = useState({ label: '', host: '127.0.0.1', port: 4455, password: '' });

  const { data: state } = useQuery({
    queryKey: ['fwtv-live-state'],
    queryFn: async () => (await api.get('/fwtv/admin/live/state')).data,
    refetchInterval: 8000,
  });
  const { data: connsData } = useQuery({
    queryKey: ['fwtv-live-connections'],
    queryFn: async () => (await api.get('/fwtv/admin/live/connections')).data,
    refetchInterval: 15000,
  });
  const { data: progsData } = useQuery({
    queryKey: ['fwtv-programmes'],
    queryFn: async () => (await api.get('/fwtv/admin/programmes')).data,
  });
  const { data: obsStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['fwtv-obs-status', connId],
    queryFn: async () => connId ? (await api.get(`/fwtv/admin/live/obs-status/${connId}`)).data : null,
    enabled: !!connId,
    refetchInterval: connId ? 6000 : false,
  });

  const conns = connsData?.connections || [];
  const programmes = progsData?.programmes || [];
  const live = state?.live || [];

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['fwtv-live-state'] });
    qc.invalidateQueries({ queryKey: ['fwtv-dashboard'] });
    qc.invalidateQueries({ queryKey: ['fwtv-programmes'] });
  };

  const goLive = async () => {
    if (!progId) return toast.error('Pick a programme first');
    try {
      const res = await api.post('/fwtv/admin/live/go-live', {
        programme_id: progId,
        connection_id: connId || null,
        start_stream: !!startStream,
        scene_name: scene || null,
        stream_url: streamUrl || null,
      });
      toast.success('Programme is live');
      if (res.data.obs?.attempted && !res.data.obs?.success) toast.error(`OBS: ${res.data.obs?.error || 'call failed'}`);
      invalidateAll();
    } catch (e) { toast.error(getErr(e)); }
  };

  const endLive = async (id) => {
    try {
      await api.post('/fwtv/admin/live/end-live', {
        programme_id: id || null,
        connection_id: connId || null,
        stop_stream: !!startStream,
      });
      toast.success('Live ended');
      invalidateAll();
    } catch (e) { toast.error(getErr(e)); }
  };

  const switchScene = async () => {
    if (!connId || !scene) return toast.error('Pick a connection + scene');
    try {
      const res = await api.post('/fwtv/admin/live/scene', { connection_id: connId, scene_name: scene });
      if (res.data.success) toast.success(`Switched to ${scene}`);
      else toast.error(res.data.error || 'OBS refused');
    } catch (e) { toast.error(getErr(e)); }
  };

  const pushLowerThird = async () => {
    if (!connId || !ltSource) return toast.error('Pick a connection + source');
    try {
      const res = await api.post('/fwtv/admin/live/lower-third', {
        connection_id: connId, source_name: ltSource, text: ltText,
      });
      if (res.data.success) toast.success('Text pushed');
      else toast.error(res.data.error || 'OBS refused');
    } catch (e) { toast.error(getErr(e)); }
  };

  const addConnection = async () => {
    if (!obsForm.label) return toast.error('Label required');
    try {
      await api.post('/obs/connections', {
        tenant_id: 'faith-works-tv',
        label: obsForm.label,
        host: obsForm.host,
        port: Number(obsForm.port),
        password: obsForm.password || null,
      });
      toast.success('Connection added');
      setObsForm({ label: '', host: '127.0.0.1', port: 4455, password: '' });
      qc.invalidateQueries({ queryKey: ['fwtv-live-connections'] });
    } catch (e) { toast.error(getErr(e)); }
  };

  const deleteConn = async (id) => {
    if (!window.confirm('Delete this OBS connection?')) return;
    try {
      await api.delete(`/obs/connections/${id}`);
      if (connId === id) setConnId('');
      qc.invalidateQueries({ queryKey: ['fwtv-live-connections'] });
    } catch (e) { toast.error(getErr(e)); }
  };

  const testConn = async (id) => {
    try {
      const res = await api.post(`/obs/connections/${id}/test`);
      if (res.data.success) toast.success(`OBS online — ${res.data.scenes?.length || 0} scenes`);
      else toast.error(res.data.error || 'OBS unreachable');
      qc.invalidateQueries({ queryKey: ['fwtv-live-connections'] });
      if (connId === id) refetchStatus();
    } catch (e) { toast.error(getErr(e)); }
  };

  return (
    <div className="space-y-4">
      {/* Live status banner */}
      <Card data-testid="fwtv-live-status">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            {live.length ? <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> ON AIR</span> : 'Off air'}
          </CardTitle>
          <CardDescription className="text-xs">
            {live.length ? `${live.length} programme(s) live now` : 'Nothing is live. Pick a programme below to go live.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {live.map((p) => (
            <div key={p.id} className="flex items-center justify-between border rounded p-2" data-testid={`fwtv-live-row-${p.id}`}>
              <div>
                <div className="font-medium text-sm">{p.title}</div>
                <div className="text-xs text-slate-500">Started {p.live_started_at ? new Date(p.live_started_at).toLocaleTimeString() : ''}
                  {p.stream_url && <> · <a className="underline" href={p.stream_url} target="_blank" rel="noopener noreferrer">stream URL</a></>}
                </div>
              </div>
              <Button size="sm" variant="destructive" onClick={() => endLive(p.id)} data-testid={`fwtv-end-live-${p.id}`}>
                <Square className="h-3 w-3 mr-1" /> End
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Go-Live control */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Radio className="h-4 w-4" /> Go live</CardTitle>
          <CardDescription className="text-xs">Flip a programme to live and (optionally) start the OBS stream in one click.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Programme</Label>
            <select className="w-full border rounded h-9 px-2 text-sm" value={progId} onChange={(e) => setProgId(e.target.value)} data-testid="fwtv-live-programme-select">
              <option value="">— pick a programme —</option>
              {programmes.map((p) => (
                <option key={p.id} value={p.id}>{p.title}{p.status !== 'published' ? ' (draft)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">OBS connection (optional)</Label>
            <select className="w-full border rounded h-9 px-2 text-sm" value={connId} onChange={(e) => setConnId(e.target.value)} data-testid="fwtv-live-connection-select">
              <option value="">— stream elsewhere (no OBS) —</option>
              {conns.map((c) => (
                <option key={c.id} value={c.id}>{c.label} · {c.host}:{c.port}{c.last_status ? ` · ${c.last_status}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Stream embed URL (YouTube live / Vimeo / Cloudflare Stream)</Label>
            <Input placeholder="https://www.youtube.com/watch?v=..." value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} data-testid="fwtv-live-stream-url" />
          </div>
          <div>
            <Label className="text-xs">OBS scene (optional)</Label>
            <Input placeholder={obsStatus?.scenes?.length ? obsStatus.scenes.join(' | ') : 'e.g. LiveWorship'} value={scene} onChange={(e) => setScene(e.target.value)} data-testid="fwtv-live-scene-input" />
          </div>
          <label className="flex items-center gap-2 text-xs col-span-full">
            <Switch checked={startStream} onCheckedChange={setStartStream} data-testid="fwtv-live-start-stream" />
            Also send <code className="mx-1">StartStream</code> to OBS
          </label>
          <div className="col-span-full">
            <Button className="bg-red-600 hover:bg-red-700" onClick={goLive} data-testid="fwtv-live-go-btn">
              <Play className="h-4 w-4 mr-1" /> Go live
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OBS controls */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Wifi className="h-4 w-4" /> OBS control</CardTitle>
          <CardDescription className="text-xs">
            {connId ? (obsStatus?.success ? `Online · scene: ${obsStatus.current_scene}${obsStatus.streaming ? ' · STREAMING' : ''}${obsStatus.recording ? ' · REC' : ''}` : `Offline · ${obsStatus?.error || 'no OBS status yet'}`) : 'Select an OBS connection above to enable controls.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Switch scene</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {(obsStatus?.scenes || []).map((s) => (
                <Button key={s} size="sm" variant={s === obsStatus?.current_scene ? 'default' : 'outline'} onClick={() => { setScene(s); }} data-testid={`fwtv-scene-btn-${s}`}>{s}</Button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Scene name" value={scene} onChange={(e) => setScene(e.target.value)} data-testid="fwtv-scene-input" />
              <Button size="sm" onClick={switchScene} disabled={!connId} data-testid="fwtv-switch-scene-btn">Switch</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Lower-third text push</Label>
            <div className="grid grid-cols-1 gap-2 mt-1">
              <Input placeholder="OBS text-source name (e.g. LowerThird)" value={ltSource} onChange={(e) => setLtSource(e.target.value)} data-testid="fwtv-lt-source" />
              <Textarea rows={2} placeholder='Sermon title / scripture reference / speaker name' value={ltText} onChange={(e) => setLtText(e.target.value)} data-testid="fwtv-lt-text" />
              <Button size="sm" onClick={pushLowerThird} disabled={!connId} data-testid="fwtv-lt-push">
                <Send className="h-3 w-3 mr-1" /> Push text
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connections manager */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">OBS connections</CardTitle>
          <CardDescription className="text-xs">Each studio / campus can register its own OBS instance. Stream keys live on the operator&apos;s machine — we only hold the WebSocket credentials.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <Input placeholder="Label" value={obsForm.label} onChange={(e) => setObsForm({ ...obsForm, label: e.target.value })} data-testid="fwtv-obs-label" />
            <Input placeholder="Host" value={obsForm.host} onChange={(e) => setObsForm({ ...obsForm, host: e.target.value })} data-testid="fwtv-obs-host" />
            <Input placeholder="Port" type="number" value={obsForm.port} onChange={(e) => setObsForm({ ...obsForm, port: e.target.value })} data-testid="fwtv-obs-port" />
            <Input placeholder="Password" type="password" value={obsForm.password} onChange={(e) => setObsForm({ ...obsForm, password: e.target.value })} data-testid="fwtv-obs-password" />
            <Button onClick={addConnection} data-testid="fwtv-obs-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          {conns.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-6">No OBS connections yet.</div>
          ) : (
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left p-2">Label</th>
                  <th className="text-left p-2">Host:Port</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Password</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {conns.map((c) => (
                    <tr key={c.id} className="border-t" data-testid={`fwtv-conn-row-${c.id}`}>
                      <td className="p-2">{c.label}</td>
                      <td className="p-2">{c.host}:{c.port}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={c.last_status === 'online' ? 'text-emerald-700 border-emerald-400' : c.last_status === 'offline' ? 'text-red-700 border-red-400' : ''}>
                          {c.last_status || 'unknown'}
                        </Badge>
                      </td>
                      <td className="p-2">{c.has_password ? 'set' : '—'}</td>
                      <td className="p-2 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => testConn(c.id)} data-testid={`fwtv-conn-test-${c.id}`}>Test</Button>
                        <Button size="sm" variant={connId === c.id ? 'default' : 'ghost'} onClick={() => setConnId(c.id)} data-testid={`fwtv-conn-select-${c.id}`}>{connId === c.id ? 'Selected' : 'Select'}</Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteConn(c.id)} data-testid={`fwtv-conn-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};



const EmbedInstructions = () => {
  const src = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api/fwtv/public/embed.js`;
  const podcast = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api/fwtv/public/podcast.xml`;
  const snippet = `<script src="${src}" defer></script>`;
  return (
  <Card data-testid="fwtv-embed-instructions"><CardHeader><CardTitle className="text-base">Public site integration</CardTitle>
    <CardDescription>Paste ONE script tag on every page of <code>faithworldministries.com/faith-works-tv/</code></CardDescription>
  </CardHeader><CardContent className="space-y-3">
    <div>
      <Label className="text-xs">Script tag</Label>
      <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto" data-testid="fwtv-embed-script">{snippet}</pre>
    </div>
    <div>
      <Label className="text-xs">Podcast feed URL (submit to Apple / Spotify / Google Podcasts)</Label>
      <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto" data-testid="fwtv-embed-podcast-url">{podcast}</pre>
    </div>
    <div>
      <p className="text-xs text-slate-600 mb-1">Drop these markers wherever you want data to appear. All are safe to place multiple times per page.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
        <code>{'<div data-fwtv="content" data-slot="hero"></div>'}</code>
        <code>{'<div data-fwtv="live"></div>'}</code>
        <code>{'<div data-fwtv="schedule"></div>'}</code>
        <code>{'<div data-fwtv="programmes" data-featured="true" data-limit="9"></div>'}</code>
        <code>{'<div data-fwtv="sermons" data-featured="true" data-limit="12"></div>'}</code>
        <code>{'<div data-fwtv="speakers" data-featured="true"></div>'}</code>
        <code>{'<div data-fwtv="categories"></div>'}</code>
        <code>{'<div data-fwtv="playlists" data-featured="true"></div>'}</code>
        <code>{'<div data-fwtv="playlist" data-slug="sunday-mornings"></div>'}</code>
        <code>{'<div data-fwtv="search" data-type="all" data-placeholder="Find a sermon"></div>'}</code>
        <code>{'<div data-fwtv="podcast-feed" data-label="Subscribe on Apple Podcasts"></div>'}</code>
        <code>{'<div data-fwtv="transcript" data-sermon-slug="my-sermon"></div>'}</code>
        <code>{'<div data-fwtv="clips" data-sermon-slug="my-sermon"></div>'}</code>
        <code>{'<div data-fwtv="prayer-form"></div>'}</code>
        <code>{'<div data-fwtv="prayer-wall" data-limit="20"></div>'}</code>
      </div>
    </div>
    <p className="text-xs text-slate-500">Available CMS slots: {Object.keys(SLOT_LABELS).join(', ')}</p>
  </CardContent></Card>);
};
